import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "../api";

export const CityAutocomplete = ({ city, country, onSelect, className, placeholder = "City (e.g. Lisbon)" }) => {
  const [q, setQ] = useState(city ? `${city}${country ? `, ${country}` : ""}` : "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(Boolean(city));
  const boxRef = useRef(null);

  // A parent may work the city out after mount (a city invite link, a saved trip). Follow it in,
  // or the field sits empty while the form quietly holds a value the shop never sees.
  useEffect(() => {
    if (!city) return;
    setQ(`${city}${country ? `, ${country}` : ""}`);
    setPicked(true);
    setOpen(false);
  }, [city, country]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get("/geocode/cities", { params: { q: q.trim() } })
        .then((r) => { setResults(r.data || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, picked]);

  const pick = (r) => {
    setQ(r.label);
    setPicked(true);
    setOpen(false);
    onSelect(r);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className={className || "flex items-center gap-3 border border-border rounded-xl px-4 py-3"}>
        {searching ? <Loader2 size={18} className="text-muted-foreground animate-spin shrink-0" /> : <MapPin size={18} className="text-muted-foreground shrink-0" />}
        <input required value={q}
          onChange={(e) => { setQ(e.target.value); setPicked(false); onSelect(null); }}
          onFocus={() => { if (results.length && !picked) setOpen(true); }}
          placeholder={placeholder} data-testid="city-autocomplete-input"
          className="w-full bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden py-1" data-testid="city-suggestions">
          {results.map((r, i) => (
            <button key={r.label} type="button" onClick={() => pick(r)} data-testid={`city-suggestion-${i}`}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-sm text-foreground hover:bg-secondary transition-colors">
              <MapPin size={14} className="text-[#16a34a] shrink-0" />
              <span className="truncate">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
